import { useViewPort } from "@website/hooks/viewport";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import type {
  FilterResultType,
  FiltersSchemaType
} from "@website/widget/filter/filter.types";
import cloneDeep from "lodash-es/cloneDeep";
import debounce from "lodash-es/debounce";
import flatMap from "lodash-es/flatMap";
import fromPairs from "lodash-es/fromPairs";
import get from "lodash-es/get";
import isArray from "lodash-es/isArray";
import isEmpty from "lodash-es/isEmpty";
import mapValues from "lodash-es/mapValues";
import omit from "lodash-es/omit";
import omitBy from "lodash-es/omitBy";
import pickBy from "lodash-es/pickBy";
import toNumber from "lodash-es/toNumber";
import toPairs from "lodash-es/toPairs";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef } from "react";

type UseFiltersProperties = {
  schema: FiltersSchemaType;
  scroll?: boolean;
};

export const useFilters = ({ schema, scroll = true }: UseFiltersProperties) => {
  const router = useRouter();
  const setFiltersQueryParameters = useCallback(
    (filtersUpdateObject: Record<string, string | Array<string>>) =>
      router.replace(
        {
          pathname: router.pathname,
          query: deepOmit(
            {
              ...router.query,
              ...filtersUpdateObject
            },
            isFilled
          )
        },
        undefined,
        { scroll, shallow: true }
      ),
    [router]
  );

  const setFiltersQueryParametersWithDebounce = useMemo(
    () =>
      debounce(
        (fieldName, adaptedQueryValue) =>
          setFiltersQueryParameters({
            [fieldName]: adaptedQueryValue
          }),
        500
      ),
    [setFiltersQueryParameters]
  );

  const setFilterQueries = useCallback(
    ({ name, asQuery, withDebounce }: FilterResultType) =>
      withDebounce
        ? setFiltersQueryParametersWithDebounce(name, asQuery)
        : setFiltersQueryParameters({
            [name]: asQuery
          } as Record<string, string | Array<string>>),
    [setFiltersQueryParameters, setFiltersQueryParametersWithDebounce]
  );

  const initialFiltersValues = fromPairs(
    toPairs(schema).map(([name, schema]) => {
      const value = router.query[name];
      if (isArray(value)) return [name, undefined];
      switch (schema.type) {
        case "checkbox":
        case "visa": {
          return [name, value?.split(",")];
        }
        case "multi-checkbox": {
          const { options } = schema;
          const array: Array<string> = [];
          options
            ?.map((item) => router.query[item.key]?.toString().split(","))
            .map(
              (items) => items?.map((item) => Boolean(item) && array.push(item))
            );
          return [name, array];
        }

        case "switch":
        case "counter": {
          return [name, value ?? get(schema, "defaultValue")];
        }

        case "radio": {
          return [name, value ?? get(schema, "defaultValue")];
        }

        case "range": {
          const { min_key, max_key } = schema;
          const [min, max] = [router.query[min_key], router.query[max_key]];

          return [
            name,
            [toNumber(min), toNumber(max)].filter(Boolean) ??
              get(schema, "defaultValue")
          ];
        }
        case "input": {
          const { search_key } = schema;
          const id = router.query[search_key];

          return [name, id ?? ""];
        }

        case "time-range": {
          const { min_key, max_key } = schema;
          const [min_source, max_source] = [
            router.query[min_key[0]],
            router.query[max_key[0]]
          ];
          const [min_destination, max_destination] = [
            router.query[min_key[1]],
            router.query[max_key[1]]
          ];
          const array: Array<number> = [];
          [
            [toNumber(min_source), toNumber(max_source)],
            [toNumber(min_destination), toNumber(max_destination)]
          ].map(
            (items) => items?.map((item) => Boolean(item) && array.push(item))
          );

          return [name, array];
        }
        default: {
          return [name, undefined];
        }
      }
    })
  );

  const selectedFiltersCount = flatMap(
    omitBy(
      initialFiltersValues,
      (value, key) =>
        !value ||
        !isFilled(value) ||
        (isArray(value) && value.length === 0) ||
        (get(schema, `${key}.defaultValue`) &&
          value === get(schema, `${key}.defaultValue`)) ||
        Number(value) === Number(get(schema, `${key}.defaultValue`))
    )
  ).length;

  return { selectedFiltersCount, setFilterQueries };
};

export const useFilterLayoutEffect = () => {
  const formReference = useRef<HTMLFormElement>(null);
  const lastScrollPosition = useRef(0);
  const { isMobile } = useViewPort();

  useEffect(() => {
    const syncScrollBar = () => {
      if (!isMobile && formReference.current) {
        formReference.current.scrollTop +=
          window.scrollY - lastScrollPosition.current;
        lastScrollPosition.current = window.scrollY;
      }
    };
    window.addEventListener("scroll", syncScrollBar);
    return () => window.removeEventListener("scroll", syncScrollBar);
  }, [isMobile]);

  return { formRef: formReference, isMobile };
};

export function useCityFilterState() {
  const router = useRouter();

  const filterQueryName = "filters_visible";

  const visible = router.query[filterQueryName] === "true";

  const onOpenFilters = () =>
    router.push(
      {
        pathname: router.pathname,
        query: deepOmit(
          { [filterQueryName]: true, ...omit(router.query, filterQueryName) },
          isFilled
        )
      },
      undefined,
      { shallow: true, scroll: false }
    );

  const onCloseFilters = () =>
    router.replace(
      {
        pathname: router.pathname,
        query: deepOmit({ ...omit(router.query, filterQueryName) }, isFilled)
      },
      undefined,
      { shallow: true, scroll: false }
    );

  return { visible, onOpenFilters, onCloseFilters };
}

export function useFilterTracking(
  callback: ({ removed }: { removed: object }) => void,
  {
    schema,
    trackFilters
  }: {
    schema: FiltersSchemaType;
    trackFilters: boolean;
  }
) {
  const router = useRouter();
  const filtersStorage = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!trackFilters) return;

    const previous = filtersStorage.current;
    const current = mapValues(schema, (_, key) => router.query[key]);

    const removed = pickBy(
      previous,
      (_v, key) => current[key] === undefined && _v !== undefined
    );

    filtersStorage.current = cloneDeep(current);
    if (!isEmpty(removed)) {
      callback({ removed });
    }
  }, [router.query, callback]);
}
